/**
 * App-level orchestration, still plain TypeScript: login, booth download, saving a section
 * (validate → local DB + outbox → sync), queueing a location. Screens call into this; the
 * platform pieces (expo-sqlite, SecureStore, NetInfo, GPS) are injected from src/platform.
 */
import { validateSection, type SectionName } from "@npac/rules";
import { ApiClient, ApiError, NetworkError } from "./api";
import { Emitter } from "./emitter";
import { validateForm, type AnyForm, type FieldError } from "./forms";
import { insideIndia, roundAccuracy, type Fix } from "./location";
import type { Session } from "./session";
import type { LocalStore } from "./store";
import { SyncEngine } from "./sync";
import type { Assembly, Booth, FieldSection, User } from "./types";
import type { Issue, SectionStatus } from "@npac/rules";

export type SaveResult =
  | { ok: true; status: SectionStatus; issues: Issue[]; synced: boolean; itemId: number }
  | { ok: false; errors: FieldError[] };

export type LocationResult = { ok: true; synced: boolean } | { ok: false; reason: "outside_india" | "too_inaccurate" };

/** What an open form was loaded from: the server version, and the outbox item if it showed an unsent edit. */
export interface FormBase {
  baseVersion: number;
  basedOnItemId: number | null;
}

export type RefreshResult = { ok: true; count: number } | { ok: false; reason: "offline" | "unauthenticated" | "error"; message?: string };

export interface AppCoreOptions {
  store: LocalStore;
  session: Session;
  deviceId: string;
  baseUrl: () => string;
  fetch?: ConstructorParameters<typeof ApiClient>[0]["fetch"];
  now?: () => number;
}

export class AppCore {
  /** session / auth changes (login, logout, 401) */
  readonly changes = new Emitter();
  readonly store: LocalStore;
  readonly session: Session;
  readonly api: ApiClient;
  readonly sync: SyncEngine;
  readonly deviceId: string;
  private readonly now: () => number;
  private authListeners = new Set<(error: ApiError | null) => void>();
  /**
   * Why the session ended, if the server ended it: `account_disabled` (the login was switched off)
   * or an expired token. Cleared by the next login. The login screen shows it.
   */
  authError: ApiError | null = null;

  constructor(opts: AppCoreOptions) {
    this.store = opts.store;
    this.session = opts.session;
    this.deviceId = opts.deviceId;
    this.now = opts.now ?? Date.now;
    this.api = new ApiClient({ baseUrl: opts.baseUrl, getToken: () => this.session.state.token, fetch: opts.fetch });
    this.sync = new SyncEngine({
      store: this.store,
      api: this.api,
      now: this.now,
      onUnauthenticated: (e) => void this.expireSession(e),
      canSend: () => !!this.session.state.token,
    });
  }

  get user(): User | null {
    return this.session.state.user;
  }

  get assemblyId(): string | null {
    return this.session.state.assemblyId;
  }

  /** Called when the server says the token is no longer valid (anywhere in the app). */
  onAuthRequired(fn: (error: ApiError | null) => void): () => void {
    this.authListeners.add(fn);
    return () => {
      this.authListeners.delete(fn);
    };
  }

  /**
   * The server rejected the token (401: expired, or `account_disabled`). Drops the token only:
   * user, cached booths, drafts and outbox stay. Also called by other senders (the field app's
   * SOS / turnout outbox) so every 401 ends the session the same way.
   */
  async expireSession(error: ApiError | null = null) {
    this.authError = error;
    await this.session.expire().catch(() => undefined);
    this.changes.emit();
    for (const fn of [...this.authListeners]) {
      try {
        fn(error);
      } catch {
        /* ignore */
      }
    }
  }

  /** Run an API call; a 401 anywhere routes the user to login (outbox and drafts stay). */
  private async guard<T>(fn: () => Promise<T>): Promise<T> {
    try {
      return await fn();
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        await this.expireSession(e);
      }
      throw e;
    }
  }

  /* ---------------- session ---------------- */

  /**
   * Log in, then download the assembly's booth list into SQLite before the UI moves on, so the
   * booth picker works offline right away. A failed download does not fail the login (the
   * picker offers a refresh).
   */
  async login(
    login: string,
    password: string,
    onPhase?: (p: "login" | "booths") => void
  ): Promise<{ user: User; assemblyChanged: boolean; booths: RefreshResult | null }> {
    const before = this.session.state.assemblyId;
    onPhase?.("login");
    const r = await this.api.login(login, password);
    // the Expo core has no screen for the admin's second step (the field app has): surveyors never get one
    if ("otpRequired" in r) throw new Error("This login needs a one-time code: use the field app or the web panel");
    await this.session.save(r.token, r.user);
    this.authError = null;
    let booths: RefreshResult | null = null;
    if (this.session.state.assemblyId) {
      onPhase?.("booths");
      booths = await this.refreshBooths();
    }
    this.changes.emit();
    void this.sync.resumeAfterLogin();
    return { user: r.user, assemblyChanged: before !== this.session.state.assemblyId, booths };
  }

  async logout(): Promise<void> {
    await this.session.clear();
    this.changes.emit();
  }

  async listAssemblies(): Promise<Assembly[]> {
    return this.guard(() => this.api.assemblies());
  }

  async chooseAssembly(id: string): Promise<RefreshResult> {
    await this.session.setAssembly(id);
    const r = await this.refreshBooths();
    this.changes.emit();
    return r;
  }

  /* ---------------- booths ---------------- */

  /** Download GET /assemblies/:aid/booths into SQLite. */
  async refreshBooths(): Promise<RefreshResult> {
    const aid = this.assemblyId;
    if (!aid) return { ok: false, reason: "error", message: "No assembly" };
    try {
      const booths = await this.guard(() => this.api.booths(aid));
      await this.store.replaceBooths(aid, booths);
      void this.prefetchAssigned(booths);
      return { ok: true, count: booths.length };
    } catch (e) {
      return this.refreshError(e);
    }
  }

  /**
   * A surveyor with a short allowedBooths list gets each booth's section data up front,
   * so forms open with the server's data even if there is no network at the booth.
   */
  private async prefetchAssigned(booths: Booth[]) {
    const allowed = this.user?.allowedBooths;
    if (!allowed || allowed.length === 0 || allowed.length > 50) return;
    for (const b of booths.filter((x) => allowed.includes(x.no))) {
      const r = await this.loadBooth(b.code);
      if (!r.ok && r.reason !== "error") return;
    }
  }

  /** Fresh booth + section data from the server (best effort; offline keeps the cache). */
  async loadBooth(code: string): Promise<RefreshResult> {
    const aid = this.assemblyId;
    if (!aid) return { ok: false, reason: "error" };
    try {
      const d = await this.guard(() => this.api.booth(aid, code));
      await this.store.putBoothDetail(aid, d.booth, d.sections);
      return { ok: true, count: 1 };
    } catch (e) {
      return this.refreshError(e);
    }
  }

  private refreshError(e: unknown): RefreshResult {
    if (e instanceof NetworkError) return { ok: false, reason: "offline", message: e.message };
    if (e instanceof ApiError && e.status === 401) return { ok: false, reason: "unauthenticated" };
    return { ok: false, reason: "error", message: e instanceof Error ? e.message : String(e) };
  }

  /* ---------------- saving ---------------- */

  /**
   * Validate with @npac/rules → write to the outbox (local DB) → clear the draft → try to sync.
   * Totals that do not match are saved (status "mismatch"); only invalid input is refused.
   */
  async saveSection(code: string, section: FieldSection, form: AnyForm, base: FormBase): Promise<SaveResult> {
    const aid = this.assemblyId;
    if (!aid) throw new Error("No assembly selected");
    const v = validateForm(section, form);
    if (!v.ok) return { ok: false, errors: v.errors };

    const id = await this.store.queueSection({
      assemblyId: aid,
      boothCode: code,
      section: section as SectionName,
      payload: { data: v.data, status: v.status, issues: v.issues },
      baseVersion: base.baseVersion,
      basedOnItemId: base.basedOnItemId,
      deviceId: this.deviceId,
    });
    await this.store.deleteDraft(aid, code, section as SectionName);

    await this.sync.refreshCounts();
    await this.sync.trigger("save").catch(() => undefined);
    const still = await this.store.getOutboxItem(id);
    return { ok: true, status: v.status, issues: v.issues, synced: !still, itemId: id };
  }

  /**
   * Save a section that has no form model of its own (the survey's sentiment, the booth's open
   * name lists): validate the data with @npac/rules → outbox → try to sync. Same path as saveSection.
   */
  async saveSectionData(code: string, section: SectionName, raw: unknown, base: FormBase): Promise<{ ok: true; synced: boolean; itemId: number; status: SectionStatus } | { ok: false; errors: { path: string; message: string }[] }> {
    const aid = this.assemblyId;
    if (!aid) throw new Error("No assembly selected");
    const v = validateSection(section, raw);
    if (!v.ok) return { ok: false, errors: v.errors };
    const id = await this.store.queueSection({
      assemblyId: aid,
      boothCode: code,
      section,
      payload: { data: v.data, status: v.status, issues: v.issues },
      baseVersion: base.baseVersion,
      basedOnItemId: base.basedOnItemId,
      deviceId: this.deviceId,
    });
    await this.store.deleteDraft(aid, code, section);
    await this.sync.refreshCounts();
    await this.sync.trigger("save").catch(() => undefined);
    const still = await this.store.getOutboxItem(id);
    return { ok: true, synced: !still, itemId: id, status: v.status };
  }

  /** Queue the chosen GPS fix (capturedAt = device time of the fix) and try to send it. */
  async shareLocation(code: string, fix: Fix): Promise<LocationResult> {
    const aid = this.assemblyId;
    if (!aid) throw new Error("No assembly selected");
    if (!insideIndia(fix.lat, fix.lng)) return { ok: false, reason: "outside_india" };
    if (fix.accuracyM != null && fix.accuracyM > 5000) return { ok: false, reason: "too_inaccurate" };
    const id = await this.store.queueLocation({
      assemblyId: aid,
      boothCode: code,
      deviceId: this.deviceId,
      payload: {
        lat: fix.lat,
        lng: fix.lng,
        accuracyM: roundAccuracy(fix.accuracyM),
        capturedAt: new Date(fix.timestamp).toISOString(),
      },
    });
    await this.sync.refreshCounts();
    await this.sync.trigger("save").catch(() => undefined);
    const still = await this.store.getOutboxItem(id);
    return { ok: true, synced: !still };
  }
}
